import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Coins, Shield, Heart } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { getUserRole } from "@/lib/userRole";

const BUNDLES = {
  cash: {
    href: "/leitfaeden/cash-bundle",
    icon: Coins,
    label: "Cash-Bundle",
    title: "Pflegekasse kürzt?",
    accentTitle: "Cash zurückholen.",
    desc: "Abrechnungs-Atlas (16 BL) + 12 Musterwidersprüche – perfektes Paar gegen Pflegekassen-Kürzungen.",
    thumbs: ["/downloads/thumbs/abrechnungs-atlas-vollversion.jpg", "/downloads/thumbs/kuerzungen-widerspruch-guide.jpg"],
    cls: "bg-white border border-brand-rose/50 hover:border-terra",
    accent: "text-brand-darkred",
    title_color: "text-sand-800",
    accent_italic: "text-terra",
    descTone: "text-sand-700",
    blob: "bg-brand-rose/25",
    btn: "bg-terra text-white group-hover:bg-terra-dark",
  },
  md: {
    href: "/leitfaeden/md-schutzschild",
    icon: Shield,
    label: "MD-Schutzschild",
    title: "MD-Prüfung in 14 Tagen?",
    accentTitle: "Schutzschild.",
    desc: "7-Tage-Vorbereitungsplan + Survival-Guide mit 23 MD-Fragen, Antwort-Mustern und 7 No-Gos.",
    thumbs: ["/downloads/thumbs/mdk-survival-guide.jpg"],
    cls: "bg-white border border-terra/40 hover:border-terra",
    accent: "text-terra",
    title_color: "text-sand-800",
    accent_italic: "text-terra",
    descTone: "text-sand-700",
    blob: "bg-peach-200/35",
    btn: "bg-terra text-white group-hover:bg-terra-dark",
  },
  pflegekraft: {
    href: "/leitfaeden/pflegekraft-bundle",
    icon: Heart,
    label: "Pflegekraft-Bundle",
    title: "Drei Werkzeuge",
    accentTitle: "für deine Patient:innen.",
    desc: "Pflegegrad-Spielbuch + Demenz · 25 Sätze + Sturz-Akutleitfaden – das B2C-Geschenkpaket.",
    thumbs: ["/downloads/thumbs/pflegegrad-spielbuch.jpg", "/downloads/thumbs/angehoerigen-mini-leitfaden.jpg"],
    cls: "bg-white border border-brand-rose/50 hover:border-terra",
    accent: "text-brand-darkred",
    title_color: "text-sand-800",
    accent_italic: "text-terra",
    descTone: "text-sand-700",
    blob: "bg-brand-rose/25",
    btn: "bg-terra text-white group-hover:bg-terra-dark",
  },
};

const B2C_ROLES = new Set(["pflegekraft", "angehoerig"]);
const B2B_ROLES = new Set(["pdl", "leitung", "qm", "inhaber", "abrechnung"]);

function BundleCard({ data, size = "compact", testId }) {
  const Icon = data.icon;
  const big = size === "big";
  return (
    <Link
      to={data.href}
      data-testid={testId}
      className={`group relative block rounded-3xl text-sand-800 overflow-hidden transition-colors ${data.cls} ${big ? "p-9 md:p-12" : "p-7 md:p-9"}`}
    >
      <div className={`absolute -top-16 -right-16 rounded-full blur-2xl pointer-events-none ${data.blob} ${big ? "w-72 h-72" : "w-56 h-56"}`} />
      <div className="relative">
        <div className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold mb-4 ${data.accent}`}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} /> {data.label}
        </div>
        <h3 className={`font-magazine leading-tight ${data.title_color || "text-sand-800"} ${big ? "text-3xl md:text-[2.2rem]" : "text-2xl"}`}>
          {data.title} <span className={`italic ${data.accent_italic || data.accent}`}>{data.accentTitle}</span>
        </h3>
        <p className={`mt-4 leading-relaxed ${data.descTone} ${big ? "text-[15px] md:text-[16px] max-w-xl" : "text-[14px]"}`}>
          {data.desc}
        </p>
        <span className={`mt-5 inline-flex items-center gap-2 rounded-full px-5 h-10 text-[13px] font-semibold transition-colors ${data.btn}`}>
          Bundle anfordern <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

/**
 * Last-Chance-Konversions-Sektion auf der Startseite. Layout passt sich der
 * bisherigen Funnel-Rolle des Besuchers an: B2C → Pflegekraft-Bundle als Hero,
 * B2B → Cash-Bundle als Hero, unbekannt → Default Cash + MD nebeneinander.
 */
export default function BundleBooster() {
  const [role, setRole] = useState(null);
  useEffect(() => { setRole(getUserRole()); }, []);

  const isB2C = role && B2C_ROLES.has(role);
  const isB2B = role && B2B_ROLES.has(role);

  let hero = null;
  let secondary = [BUNDLES.cash, BUNDLES.md];
  if (isB2C) {
    hero = BUNDLES.pflegekraft;
    secondary = [BUNDLES.cash, BUNDLES.md];
  } else if (isB2B) {
    hero = BUNDLES.cash;
    secondary = [BUNDLES.md, BUNDLES.pflegekraft];
  }

  return (
    <section
      id="bundles"
      data-testid="home-bundle-booster"
      className="bg-cream-50 py-24 md:py-36 border-t border-cream-300"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-brand-darkred mb-3">
              {hero ? "Speziell für dich" : "Du hast wenig Zeit?"}
            </div>
            <h2 className="font-magazine text-3xl md:text-[2.4rem] text-sand-800 leading-[1.15]">
              {hero ? (
                <>Das passende <span className="text-terra italic">Bundle</span> zuerst.</>
              ) : (
                <>Die zwei wichtigsten <span className="text-terra italic">Premium-Bundles</span>.</>
              )}
            </h2>
            <p className="mt-5 text-sand-700 text-[15px] leading-[1.7]">
              {hero
                ? "Wir haben dein Bundle nach vorne gestellt – die anderen findest du direkt darunter. Vollversionen gratis im Dickies-Campus."
                : "Je 2 aufeinander abgestimmte Leitfäden, eine E-Mail, sofort als PDF. Die kompletten Vollversionen liegen gratis im Dickies-Campus – Helden-Basic reicht (0\u00A0€)."}
            </p>
          </div>
        </Reveal>

        {hero && (
          <Reveal>
            <div className="mt-12" data-testid={`home-booster-hero-${role}`}>
              <BundleCard data={hero} size="big" testId={`home-booster-hero-card-${role}`} />
            </div>
          </Reveal>
        )}

        <div className={`grid lg:grid-cols-2 gap-5 md:gap-7 ${hero ? "mt-5 md:mt-7" : "mt-12"}`}>
          {secondary.map((b, i) => (
            <Reveal key={b.href} delay={i * 80}>
              <BundleCard data={b} testId={`home-booster-secondary-${b.label.toLowerCase().replace(/\s+/g, "-").split("-")[0]}`} />
            </Reveal>
          ))}
        </div>

        <p className="mt-7 text-center text-[12.5px] text-sand-500">
          Alle Leitfäden im Überblick:{" "}
          <Link to="/leitfaeden" data-testid="home-booster-all" className="underline text-sand-700 hover:text-terra">
            Helden-Bibliothek &rarr;
          </Link>
        </p>
      </div>
    </section>
  );
}
